import React, { useEffect } from "react";
import { toast, LoadingIndicator } from "@jsq/ds/core";
import { addError } from "@jsq/observability";
import { formatError } from "#common/hooks/useReportError";
import { PayeeActionTypes } from "../../../PayeeWorkflows/Context/constants";
import { usePayeeDispatchContext } from "../../../PayeeWorkflows/Context/PayeeContext";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { type ChildComponentProps } from "../types";
import { useCreatePayeePaymentPref } from "./hooks";
import {
  compareAchPaymentPrefsComparableItems,
  compareWirePaymentPrefsComparableItems,
} from "./utils";

export const PayeePaymentPrefCanopyAutoConfirmation: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
}) => {
  const { paymentPrefDetail, arena, selectedPaymentMethod, initialPaymentPrefDetail } =
    useConsolidatedPaymentPrefContext();
  const payeeDispatch = usePayeeDispatchContext();

  // Use the unified hook that handles both ACH and Wire
  const createPayeePaymentPref = useCreatePayeePaymentPref(selectedPaymentMethod);

  useEffect(() => {
    const processPaymentPref = async () => {
      if (!paymentPrefDetail || !arena?.id || !selectedPaymentMethod) {
        toast.error("Missing required information");
        onBackClick();
        return;
      }

      // Check if payment pref matches existing one - skip creation if unchanged
      if (initialPaymentPrefDetail) {
        let isUnchanged = false;

        if (
          paymentPrefDetail.__typename === "WirePaymentPrefObjectType" &&
          initialPaymentPrefDetail.__typename === "WirePaymentPrefObjectType"
        ) {
          isUnchanged = compareWirePaymentPrefsComparableItems(
            initialPaymentPrefDetail,
            paymentPrefDetail,
          );
        } else if (
          paymentPrefDetail.__typename === "AchPaymentPrefObjectType" &&
          initialPaymentPrefDetail.__typename === "AchPaymentPrefObjectType"
        ) {
          isUnchanged = compareAchPaymentPrefsComparableItems(
            initialPaymentPrefDetail,
            paymentPrefDetail,
          );
        }

        if (isUnchanged) {
          onContinueClick();
          return;
        }
      }

      try {
        const paymentPrefFull = await createPayeePaymentPref(paymentPrefDetail, arena.id);

        if (!paymentPrefFull) {
          addError(formatError("createPayeePaymentPref returned null"));
          toast.error("Error creating payment pref instructions. Please try again later.");
          onBackClick();
          return;
        }

        // Store the created payment preference in the context for display in the review step
        payeeDispatch({
          type: PayeeActionTypes.SET_PAYMENT_PREF,
          payload: { paymentPref: paymentPrefFull },
        });

        onContinueClick();
      } catch (e) {
        addError(formatError(e));
        toast.error("Error creating new payment instructions. Please try again later.");
        onBackClick();
        return;
      }
    };
    void processPaymentPref();
  }, []);
  return <LoadingIndicator />;
};
